// ─────────────────────────────────────────────
//  PrimaryFacilityPrompt — first-login question:
//  "Which facility do you work at most often?"
//  Saves users.primaryFacilityId (self-writable field), which orders every
//  provider dropdown app-wide (their home facility's people first — see
//  domain/staff.sortPoolByPrimaryFacility via useStaff). Asked once: only
//  when the signed-in profile has no primaryFacilityId yet and more than one
//  facility is active. Also aligns activeFacilityId so they land on their
//  own board.
// ─────────────────────────────────────────────
import React, { useState } from "react";
import { useAuth } from "../context/AuthContext";
import { useToast } from "../context/ToastContext";
import { useFacilities } from "../hooks/useFacilities";
import { updateUser } from "../firebase/firestore";
import { resolveFacilityStatus } from "../domain/facilityRules";
import { facilityLabel } from "../domain/facilities";
import Modal from "./Modal";
import Button from "./Button";
import tokens from "../theme/tokens";

const { color } = tokens;

export default function PrimaryFacilityPrompt() {
  const { realUser } = useAuth();
  // Mount the inner hook tree only AFTER sign-in: useFacilities subscribes on
  // mount, and a pre-auth subscription (app shell renders on /login) is denied
  // and never retried — the same cold-login trap useStaff once had.
  if (!realUser?.uid || realUser.primaryFacilityId) return null;
  return <PromptInner realUser={realUser} />;
}

function PromptInner({ realUser }) {
  const toast = useToast();
  const allFacilities = useFacilities();
  const [choice, setChoice] = useState("");
  const [saving, setSaving] = useState(false);
  // "Not now" quiets the question for the rest of the browser session (it
  // would otherwise re-ask on every navigation); it returns next sign-in.
  const DISMISS_KEY = "atc-primary-facility-dismissed";
  const [dismissed, setDismissed] = useState(
    () => sessionStorage.getItem(DISMISS_KEY) === "1"
  );
  const dismiss = () => {
    sessionStorage.setItem(DISMISS_KEY, "1");
    setDismissed(true);
  };

  const active = allFacilities.filter((f) => resolveFacilityStatus(f) === "active");
  const open = !dismissed && active.length > 1;

  if (!open) return null;

  const labelOf = (f) => {
    const known = facilityLabel(f.id);
    return known !== f.id ? known : f.label || f.name || f.id;
  };

  const save = async () => {
    if (!choice) return;
    setSaving(true);
    try {
      const f = active.find((x) => x.id === choice);
      await updateUser(realUser.uid, {
        primaryFacilityId: choice,
        activeFacilityId: choice,
        activeFacilityName: f ? labelOf(f) : choice,
      });
      toast.success("Saved — your facility's providers now list first in every dropdown.");
    } catch (e) {
      console.error("[PrimaryFacilityPrompt] save failed:", e);
      toast.error("Could not save your facility. You can set it later from your profile.");
      dismiss(); // never trap the user behind a failing modal
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal
      isOpen
      onClose={dismiss}
      title="Which facility do you work at most often?"
      maxWidth={420}
      footer={
        <>
          <Button variant="secondary" onClick={dismiss} disabled={saving}>
            Not now
          </Button>
          <Button onClick={save} disabled={saving || !choice}>
            {saving ? "Saving…" : "Save"}
          </Button>
        </>
      }
    >
      <p style={{ fontSize: 13, color: color.text.secondary, margin: "0 0 12px" }}>
        We'll list your facility's providers first in every dropdown — scheduling,
        relief, assignments — so picking people is faster.
      </p>
      <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
        {active.map((f) => (
          <label
            key={f.id}
            style={{
              display: "flex", alignItems: "center", gap: 10, padding: "10px 12px",
              borderRadius: 8, cursor: "pointer",
              border: `1px solid ${choice === f.id ? color.accent.primary : color.border.default}`,
              background: choice === f.id ? "#1e40af10" : color.surface.raised,
              fontSize: 13, fontWeight: 700, color: color.text.primary,
            }}
          >
            <input
              type="radio"
              name="primary-facility"
              value={f.id}
              checked={choice === f.id}
              onChange={() => setChoice(f.id)}
            />
            {labelOf(f)}
          </label>
        ))}
      </div>
    </Modal>
  );
}
